// filepath: apps/web/src/features/admin/effects/HttpEndpointsTable.tsx
//
// FR-UI-ADM-8 -- data table for the HTTP endpoint admin editor.
//
// Status badge carries `data-status` ("active"|"disabled"),
// an icon, and a label (FR-UI-CORE-9 AC-2 -- color is NOT sole carrier).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { HttpEndpoint } from "./types";

interface HttpEndpointsTableProps {
  endpoints: HttpEndpoint[];
  onDisable: (id: string, version: number) => void | Promise<void>;
  onDelete: (id: string, version: number) => void | Promise<void>;
  isPending: boolean;
}

export function HttpEndpointsTable({
  endpoints,
  onDisable,
  onDelete,
  isPending,
}: HttpEndpointsTableProps): React.ReactElement {
  const { t } = useTranslation();

  if (endpoints.length === 0) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="http-endpoints-empty"
      >
        {t("admin.effects.http.empty")}
      </p>
    );
  }

  return (
    <table
      className="w-full border-collapse text-sm"
      data-testid="http-endpoints-table"
      aria-label={t("admin.effects.http.table_aria_label")}
    >
      <thead>
        <tr className="border-b text-left text-xs uppercase text-muted-foreground">
          <th className="px-3 py-2">{t("admin.effects.http.column.url")}</th>
          <th className="px-3 py-2">{t("admin.effects.http.column.method")}</th>
          <th className="px-3 py-2">{t("admin.effects.http.column.status")}</th>
          <th className="px-3 py-2">
            {t("admin.effects.http.column.actions")}
          </th>
        </tr>
      </thead>
      <tbody>
        {endpoints.map((ep) => (
          <tr
            key={ep.id}
            className="border-b hover:bg-muted/40"
            data-testid={`http-endpoint-row-${ep.id}`}
          >
            <td className="px-3 py-2 font-mono text-xs">{ep.url}</td>
            <td className="px-3 py-2 text-xs uppercase">{ep.method}</td>
            <td className="px-3 py-2">
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                data-status={ep.status}
              >
                <span aria-hidden="true">
                  {ep.status === "active" ? "●" : "○"}
                </span>
                {t(`admin.effects.status.${ep.status}`)}
              </span>
            </td>
            <td className="px-3 py-2">
              <div className="flex gap-2">
                {ep.status === "active" && (
                  <button
                    type="button"
                    className="min-h-[48px] min-w-[48px] rounded border border-border px-2 py-1 text-xs"
                    aria-label={t("admin.effects.http.action.disable")}
                    disabled={isPending}
                    onClick={() => void onDisable(ep.id, ep.row_version)}
                    data-testid={`http-endpoint-disable-${ep.id}`}
                  >
                    {t("admin.effects.http.action.disable")}
                  </button>
                )}
                <button
                  type="button"
                  className="min-h-[48px] min-w-[48px] rounded border border-destructive px-2 py-1 text-xs text-destructive"
                  aria-label={t("admin.effects.http.action.delete")}
                  disabled={isPending}
                  onClick={() => void onDelete(ep.id, ep.row_version)}
                  data-testid={`http-endpoint-delete-${ep.id}`}
                >
                  {t("admin.effects.http.action.delete")}
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
